import { createSelector } from 'reselect';
import shuffle from 'lodash/shuffle';

import { FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP } from '../../../../../constants/filters';
import { topCategories, topFormats, topDates } from '../constants';

const getSuggestions = (state) => state.suggestions;
const getEventSearch = (state) => state.search.eventSearch;

const getSuggestedFilters = createSelector(
    [getSuggestions],
    (suggestions) => suggestions.filters,
);

const getSuggestedTags = createSelector(
    [getSuggestions],
    (suggestions) => suggestions.tags,
);

const getFilterDisplayValue = (filter) => {
    const filterType = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filter.type];

    const filterValue = filterType.values.find(
        ({ value }) => value === filter.value,
    );

    return filterValue?.display;
};

const _formatFiltersForRender = (filters) =>
    shuffle(
        filters
            .filter((filter) => getFilterDisplayValue(filter))
            .map((filter) => {
                const display = getFilterDisplayValue(filter);
                return {
                    ...filter,
                    display,
                };
            }),
    );

/**
 * Parse returned filters into format suitable for the
 * consuming component.
 *
 * Obey the following rules:
 *  1. Prioritize filters present in topCategories/topFormats/topPrice
 *  2. If total list <10 after prioritization, include top remaining filters
 *  3. If there are >=3 filters, include the free filter
 *  4. Shuffle returned array
 */
export const getParsedSuggestedFilters = createSelector(
    [getSuggestedFilters, getEventSearch],
    (filters, eventSearch) => {
        if (filters.length < 4) {
            return [];
        }

        //We don't want to suggest a filter that is already applied.
        const filtersNotCurrentlyInUse = filters.filter((filter) => {
            //check eventSearch dates
            if (eventSearch.dates === filter.value) {
                return false;
            }

            if (eventSearch?.tags?.includes(filter.value)) {
                return false;
            }

            return true;
        });

        //Priortize filters defined in our top 10 lists
        let prioritizedFilterValues = [
            ...filtersNotCurrentlyInUse.filter(({ value }) =>
                [...topCategories, ...topFormats, ...topDates].includes(value),
            ),
        ];

        //append difference to end of array
        prioritizedFilterValues = [
            ...prioritizedFilterValues,
            ...filtersNotCurrentlyInUse.reduce((memo, filter) => {
                const filterInPrioritized = prioritizedFilterValues.find(
                    ({ value }) => filter.value === value,
                );
                if (filterInPrioritized) {
                    return memo;
                } else {
                    return [...memo, filter];
                }
            }, []),
        ];

        return _formatFiltersForRender([
            ...prioritizedFilterValues.slice(0, 10),
            { type: 'price', value: 'free' },
        ]);
    },
);

export const getRandomizedSuggestedTags = createSelector(
    [getSuggestedTags],
    (tags) => {
        const shuffledTags = shuffle(tags);
        return shuffledTags.slice(0, 20);
    },
);
